'use client';

import { usePathname } from 'next/navigation';
import { JSX, useCallback } from 'react';

import { ConfirmationModal } from '@videoblocks/design-system';

import { useNavigationGuard } from '../../../../../components/lib/NavigationGuardProvider';
import { useHasUnpersistedVoiceoverGeneration } from '../_hooks/useHasUnpersistedVoiceoverGeneration';

export interface VoiceoverNavigationGuardProps {
    /** Root path for this mount of the voiceover tool, e.g. '/voiceover' or '/ai/voiceover' */
    basePath: string;
}

function isWithinVoiceoverTool(to: string, basePath: string): boolean {
    return to === basePath || to.startsWith(`${basePath}/`) || to.startsWith(`${basePath}?`);
}

export default function VoiceoverNavigationGuard({ basePath }: VoiceoverNavigationGuardProps): JSX.Element {
    const hasUnpersistedGeneration = useHasUnpersistedVoiceoverGeneration();
    const pathname = usePathname();

    const shouldGuard = useCallback((to: string): boolean => !isWithinVoiceoverTool(to, basePath), [basePath]);

    const { active, accept, reject } = useNavigationGuard({
        enabled: hasUnpersistedGeneration,
        shouldGuard,
        currentUrl: pathname,
    });

    return (
        <ConfirmationModal
            isOpen={active}
            title="Warning - your voiceover has not been saved"
            content="Your voiceover cannot be recovered if you leave this page. Are you sure you want to leave without saving it?"
            confirmLabel="Leave page"
            cancelLabel="Stay on page"
            onCancel={reject}
            onConfirm={accept}
        />
    );
}
